import React, { useEffect, useState } from "react";

import { Controller } from "react-hook-form";

import CreatableSelect from "react-select/creatable";

import { buildTicketTypeOptions } from "../../config/predefinedTicketTypes";

import "./ticketTypeSelect.css";

/** Narrow viewports only — avoid `(pointer: coarse)` so hybrid Windows
 *  touchscreens don't take the mobile path on a desktop-width Add Event page. */
const TOUCH_SELECT_LAYOUT_MQ = "(max-width: 991px)";

const ticketTypeSelectStyles = {
  control: (base) => ({
    ...base,
    flexWrap: "nowrap",
    minHeight: 36,
    color: "#ffffff",
  }),
  valueContainer: (base) => ({
    ...base,
    flex: "1 1 0",
    minWidth: 0,
    overflow: "hidden",
    color: "#ffffff",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#ffffff",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "clip",
    maxWidth: "100%",
  }),
  placeholder: (base) => ({
    ...base,
    color: "rgba(255, 255, 255, 0.55)",
  }),
  input: (base) => ({
    ...base,
    color: "#ffffff",
  }),
  indicators: (base) => ({
    ...base,
    flexShrink: 0,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 200000,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 200000,
    marginTop: 0,
    marginBottom: 0,
  }),
};

const ticketTypeMenuPortalTarget =
  typeof document !== "undefined" ? document.body : null;

function useTouchSelectLayout() {
  const [isTouchLayout, setIsTouchLayout] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(TOUCH_SELECT_LAYOUT_MQ).matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(TOUCH_SELECT_LAYOUT_MQ);
    const handleChange = (event) => setIsTouchLayout(event.matches);
    setIsTouchLayout(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return isTouchLayout;
}

function getErrorByPath(errors, path) {
  if (!errors || !path) return undefined;

  return path.split(".").reduce((current, segment) => {
    if (current == null) return undefined;
    return current[segment];
  }, errors);
}

function TicketTypeCreatableInput({
  value,
  onChange,
  selectRef,
  placeholder,
  formatCreateLabel,
  className,
}) {
  const isTouchLayout = useTouchSelectLayout();

  const options = buildTicketTypeOptions(value ? [value] : []);

  const selectValue = value
    ? options.find((opt) => opt.value === value) || {
        value: String(value),
        label: String(value),
      }
    : null;

  return (
    <div className="SelectBox ticket-type-select-field">
      <CreatableSelect
        ref={selectRef}
        className={`${className} react-select-container`}
        classNamePrefix="react-select"
        styles={ticketTypeSelectStyles}
        classNames={{
          menuPortal: () => "ticket-type-select-menu-portal",
          menu: () => "ticket-type-select-menu",
        }}
        menuPortalTarget={ticketTypeMenuPortalTarget}
        menuPosition="fixed"
        menuPlacement="bottom"
        closeMenuOnScroll={false}
        menuShouldScrollIntoView={false}
        menuShouldBlockScroll={isTouchLayout}
        blurInputOnSelect
        isClearable
        isSearchable
        closeMenuOnSelect
        options={options}
        value={selectValue}
        placeholder={placeholder}
        formatCreateLabel={formatCreateLabel}
        onChange={(opt) => onChange(opt?.value ?? "")}
        onCreateOption={(input) => {
          const trimmed = input.trim().slice(0, 70);
          if (trimmed) onChange(trimmed);
        }}
      />
    </div>
  );
}

/**
 * Creatable ticket type field: pick a predefined label or type a custom one.
 * Uses the same singleSelecter theme as organiser / club dropdowns above.
 */
const TicketTypeSelect = ({
  control,
  name,
  rules,
  errors,
  forceShowError,
  placeholder,
  formatCreateLabel,
  className = "singleSelecter organiserDropdownStyle ticketTypeSelect",
  onValueChange,
}) => {
  const fieldError = getErrorByPath(errors, name);
  const hasError = forceShowError || fieldError !== undefined;

  return (
    <div className={hasError ? "ticket-type-select--invalid" : ""}>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field: { onChange, value, ref } }) => (
          <TicketTypeCreatableInput
            value={value}
            onChange={(next) => {
              onChange(next);
              onValueChange?.(next);
            }}
            selectRef={ref}
            placeholder={placeholder}
            formatCreateLabel={formatCreateLabel}
            className={className}
          />
        )}
      />
    </div>
  );
};

export default TicketTypeSelect;
